import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Tag } from 'antd';
import { formatNumberToLocaleString } from '@utils/math';
import usePipelineActions from '@hooks/usePipelineActions';
import { NOT_AVAILABLE_LABEL } from '@constants/common';
import { FilterProp, FiltersProp, FilterType } from '@constants/data-table';
import { StatusCodes } from '@constants/enum/common';
import {
  PIPELINE_USAGE_CYCLE_HEADER,
  PIPELINE_USAGE_METRICS_FILTERS_FIELD_KEYS,
  PIPELINE_USAGE_METRICS_FILTERS_TYPE_LABEL,
  PIPELINE_USAGE_METRICS_SORTING_OPTIONS,
  PIPELINE_USAGE_METRICS_TYPE_FILTERS_LABELS,
  PIPELINES_TABLE_COLUMNS_TITLE,
} from '@constants/metering';
import { IPipelineUsage, PipelineUsageType } from '@redux/types/types';
import WorkspaceLink from '@components/common/WorkspaceLink/WorkspaceLink';
import DataTable from '@components/dataTable/DataTable';
import useIndexes from '@modules/Indexes/hooks/useIndexes';
import styles from './pipelinesUsage.module.scss';
import usePipelinesUsage from '../../hooks/usePipelinesUsage';

const PAGE_SIZE = 5;

const PipelinesUsage = () => {
  const navigate = useNavigate();
  const {
    pipelineUsageMetrics,
    pipelineUsageMetricsSortValue,
    getPipelineUsageMetrics,
    fetchingPipelineUsageMetricsStatus,
  } = usePipelinesUsage();
  const { getIndexDetailsPageRoute } = useIndexes();
  const { getPipelineDetailsPageRoute } = usePipelineActions();

  const getPipelineUsageFilters = (): FiltersProp<FilterType.SELECT> => {
    const pipelineTypeFilter = {
      type: FilterType.SELECT,
      search: false,
      key: PIPELINE_USAGE_METRICS_FILTERS_FIELD_KEYS.TYPE,
      title: PIPELINE_USAGE_METRICS_FILTERS_TYPE_LABEL,
      options: [
        {
          key: PipelineUsageType.INDEX,
          label: PIPELINE_USAGE_METRICS_TYPE_FILTERS_LABELS.INDEX,
          value: PipelineUsageType.INDEX,
        },
        {
          key: PipelineUsageType.QUERY,
          label: PIPELINE_USAGE_METRICS_TYPE_FILTERS_LABELS.PIPELINE,
          value: PipelineUsageType.QUERY,
        },
      ],
      style: { minWidth: '140px' },
    } as FilterProp<FilterType.SELECT>;

    return [pipelineTypeFilter];
  };

  const onPipelineNameClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    rowItem: IPipelineUsage,
  ) => {
    event.preventDefault();

    const {
      workspace_name: workspaceName,
      pipeline_name: pipelineName,
      pipeline_type: pipelineType,
    } = rowItem;

    const baseWorkspaceRoute = `/${workspaceName}`;
    if (pipelineType === PipelineUsageType.INDEX) {
      navigate(`${baseWorkspaceRoute}${getIndexDetailsPageRoute(pipelineName)}`);
    } else {
      navigate(`${baseWorkspaceRoute}${getPipelineDetailsPageRoute(pipelineName)}`);
    }
  };

  // TODO: We might need to move to it's own component
  // to reuse it in the pipeline page
  const renderPipelineType = (type: PipelineUsageType) => {
    const mappedEnvElements = {
      [PipelineUsageType.INDEX]: {
        label: PIPELINE_USAGE_METRICS_TYPE_FILTERS_LABELS.INDEX,
      },
      [PipelineUsageType.QUERY]: {
        label: PIPELINE_USAGE_METRICS_TYPE_FILTERS_LABELS.PIPELINE,
      },
    };

    if (!type || !mappedEnvElements[type]) return null;

    return (
      <Tag bordered={false} color="processing">
        {mappedEnvElements[type].label}
      </Tag>
    );
  };

  const renderDocumentStorageUnits = (units: number, pipelineType: PipelineUsageType) => {
    if (pipelineType !== PipelineUsageType.INDEX) return NOT_AVAILABLE_LABEL;
    return formatNumberToLocaleString(units, true);
  };

  const columns = [
    {
      title: PIPELINES_TABLE_COLUMNS_TITLE.NAME,
      dataIndex: 'pipeline_name',
      key: 'pipeline_name',
      width: '40%',
      render: (name: string, rowItem: IPipelineUsage) => (
        <WorkspaceLink
          to={`/pipelines/${name}`}
          onClick={(event) => onPipelineNameClick(event, rowItem)}
          className={styles.pipelineNameLink}
        >
          {name}
        </WorkspaceLink>
      ),
    },
    {
      title: PIPELINES_TABLE_COLUMNS_TITLE.TYPE,
      dataIndex: 'pipeline_type',
      key: 'pipeline_type',
      width: '10%',
      render: (type: PipelineUsageType) => renderPipelineType(type),
    },
    {
      title: PIPELINES_TABLE_COLUMNS_TITLE.CREDITS,
      dataIndex: 'pipelines_used',
      key: 'pipelines_used',
      width: '10%',
      render: (number: number) => formatNumberToLocaleString(number, true),
    },
    {
      title: PIPELINES_TABLE_COLUMNS_TITLE.STORAGE_UNITS,
      dataIndex: 'document_storage_units',
      key: 'document_storage_units',
      width: '10%',
      render: (number: number, rowItem: IPipelineUsage) =>
        renderDocumentStorageUnits(number, rowItem.pipeline_type),
    },
    {
      title: PIPELINES_TABLE_COLUMNS_TITLE.WORKSPACE_NAME,
      dataIndex: 'workspace_name',
      key: 'workspace_name',
      width: '10%',
    },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h5>{PIPELINE_USAGE_CYCLE_HEADER}</h5>
        <Tag>{pipelineUsageMetrics.total ?? 0}</Tag>
      </div>
      <DataTable
        data={pipelineUsageMetrics.data}
        total={pipelineUsageMetrics.total}
        rowKey="pipeline_id"
        rowDisabled={(rowItem: Record<string, unknown>) => {
          return !rowItem.is_active;
        }}
        getData={(currentPage, pageSize, searchValue, sortValue, filterValues) =>
          getPipelineUsageMetrics({
            pageNumber: currentPage,
            limit: pageSize,
            searchValue,
            sortValue,
            filterValues,
          })
        }
        sorting={{
          selectedValue: pipelineUsageMetricsSortValue,
          options: PIPELINE_USAGE_METRICS_SORTING_OPTIONS,
        }}
        loading={fetchingPipelineUsageMetricsStatus === StatusCodes.IN_PROGRESS}
        filters={getPipelineUsageFilters()}
        columns={columns}
        searchAvailable
        rowSelection={false}
        pagination={{ pageSize: PAGE_SIZE }}
      />
    </div>
  );
};

export default PipelinesUsage;
